/* Registering and correcting an item — STORY-028.
 *
 * ── one form, for both ──────────────────────────────────────────────────────
 * Creating and editing ask for exactly the same fields, and two forms that
 * agree today are two forms that disagree later. The only difference is which
 * mutation it calls and what it starts with.
 *
 * ── the cost fields disappear when the books own them ───────────────────────
 * An item linked to an expense takes its cost and date from that Transaction,
 * so offering an editable field for either would be offering to write a second
 * answer — which the server and the CHECK constraint both refuse. The link
 * picker replaces them rather than sitting beside them.
 *
 * ── unlinking leaves them empty ─────────────────────────────────────────────
 * Not pre-filled with what the books said. A number copied out at the moment of
 * unlinking is the drift the link exists to prevent, reintroduced at the one
 * moment nobody is watching for it.
 */

import {
  type CreateItemRequest,
  type InventoryItem,
  ITEM_CONDITIONS,
  ITEM_NAME_MAX,
  ITEM_NOTES_MAX,
  type ItemCondition,
} from '@eventuley/contracts';
import { amountOf, parseCurrency, parseMoney } from '@eventuley/domain';
import { useId, useState } from 'react';
import { NetworkError } from '../../../shared/api/client.js';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout, MoneyInput, TextField } from '../../../shared/ui/index.js';
import { useLedger } from '../../money/index.js';
import { useCreateItem, useInventory, useUpdateItem } from '../api/index.js';
import { CONDITION_LABELS } from './return-control.js';

interface Draft {
  name: string;
  category: string;
  ownerId: string;
  holderId: string;
  condition: ItemCondition;
  notes: string;
  purchasedOn: string;
  amount: string;
  currency: string;
  transactionId: string;
}

const draftFrom = (item: InventoryItem | undefined, fallbackCurrency: string): Draft => ({
  name: item?.name ?? '',
  category: item?.category ?? '',
  ownerId: item?.owner?.userId ?? '',
  holderId: item?.holder?.userId ?? '',
  condition: item?.condition ?? 'good',
  notes: item?.notes ?? '',
  /* Only the item's OWN date and cost. A linked item holds neither, and
   * pre-filling from the books here is how a copy gets made. */
  purchasedOn: item?.costFromTransaction ? '' : (item?.purchasedOn ?? ''),
  amount: '',
  currency: item?.costFromTransaction ? fallbackCurrency : (item?.costCurrency ?? fallbackCurrency),
  transactionId: item?.transaction?.id ?? '',
});

export function ItemForm({
  organisationId,
  members,
  currencies,
  item,
  onDone,
}: {
  readonly organisationId: string;
  readonly members: readonly { userId: string; displayName: string }[];
  readonly currencies: readonly string[];
  /** Absent when registering something new. */
  readonly item?: InventoryItem;
  readonly onDone: () => void;
}) {
  const t = useTranslate();
  const { formatLocale: locale } = useI18n();
  const create = useCreateItem(organisationId);
  const update = useUpdateItem(organisationId);
  const [draft, setDraft] = useState(() => draftFrom(item, currencies[0] ?? 'USD'));
  const [amountError, setAmountError] = useState<string | null>(null);

  const conditionId = useId();
  const ownerId = useId();
  const holderId = useId();
  const expenseId = useId();

  /* Expenses not already backing an item. The link is 1:1, so offering a taken
   * one would be offering a refusal — and the item being edited keeps its own,
   * which is not "taken" from its point of view. */
  const { data: ledger } = useLedger(organisationId, { direction: 'expense' });
  const { data: inventory } = useInventory(organisationId);
  const takenBy = new Map(
    (inventory?.items ?? [])
      .filter((row) => row.transaction !== null)
      .map((row) => [row.transaction?.id as string, row.id]),
  );
  const expenses = (ledger?.transactions ?? []).filter(
    (row) => !takenBy.has(row.id) || takenBy.get(row.id) === item?.id,
  );

  const linked = draft.transactionId !== '';
  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  const submit = () => {
    setAmountError(null);

    /* Parsed once, here, into integer minor units using the digit count of the
     * chosen currency — never a constant two. ADR-0005. */
    let costMinor: number | null = null;
    if (!linked && draft.amount.trim() !== '') {
      const code = parseCurrency(draft.currency);
      const parsed = code ? parseMoney(draft.amount, code, locale) : null;
      if (!parsed?.ok) {
        setAmountError(t('inventory.cost_invalid'));
        return;
      }
      costMinor = amountOf(parsed.value);
    }

    const values: CreateItemRequest = {
      name: draft.name,
      ...(draft.category === '' ? {} : { category: draft.category }),
      ...(draft.ownerId === '' ? {} : { ownerId: draft.ownerId }),
      ...(draft.holderId === '' ? {} : { holderId: draft.holderId }),
      condition: draft.condition,
      ...(draft.notes === '' ? {} : { notes: draft.notes }),
      /* Exactly one source. Linked means the item carries neither. */
      ...(linked
        ? { transactionId: draft.transactionId }
        : {
            ...(draft.purchasedOn === '' ? {} : { purchasedOn: draft.purchasedOn }),
            ...(costMinor === null ? {} : { costMinor, costCurrency: draft.currency }),
          }),
    };

    const done = { onSuccess: () => onDone() };

    if (item) {
      update.mutate(
        {
          itemId: item.id,
          ...values,
          /* Cleared explicitly when the link is removed, so the server knows
             this is an unlink rather than a field left alone. */
          ...(linked ? {} : { transactionId: null }),
          ...(linked || draft.purchasedOn !== '' ? {} : { purchasedOn: null }),
          ...(linked || costMinor !== null ? {} : { costMinor: null, costCurrency: null }),
          ...(draft.category === '' ? { category: null } : {}),
          ...(draft.ownerId === '' ? { ownerId: null } : {}),
          ...(draft.holderId === '' ? { holderId: null } : {}),
          ...(draft.notes === '' ? { notes: null } : {}),
        },
        done,
      );
    } else {
      create.mutate(values, done);
    }
  };

  return (
    <form
      className="card"
      onSubmit={(submitted) => {
        submitted.preventDefault();
        submit();
      }}
    >
      <TextField
        label={t('inventory.name_label')}
        value={draft.name}
        required
        maxLength={ITEM_NAME_MAX}
        onChange={(changed) => setDraft({ ...draft, name: changed.target.value })}
      />
      <TextField
        label={t('inventory.category_label')}
        value={draft.category}
        onChange={(changed) => setDraft({ ...draft, category: changed.target.value })}
      />

      <div className="field">
        <label className="field__label" htmlFor={conditionId}>
          {t('inventory.condition_label')}
        </label>
        <select
          id={conditionId}
          className="field__control"
          value={draft.condition}
          onChange={(changed) =>
            setDraft({ ...draft, condition: changed.target.value as ItemCondition })
          }
        >
          {ITEM_CONDITIONS.map((condition) => (
            <option key={condition} value={condition}>
              {t(CONDITION_LABELS[condition])}
            </option>
          ))}
        </select>
      </div>

      {/* Owner and holder are two controls, deliberately. They are equal most
          of the time, which is exactly why one field would seem enough — and
          the case where they differ is the point of the module. */}
      <div className="field">
        <label className="field__label" htmlFor={ownerId}>
          {t('inventory.owner_label')}
        </label>
        <select
          id={ownerId}
          className="field__control"
          value={draft.ownerId}
          onChange={(changed) => setDraft({ ...draft, ownerId: changed.target.value })}
        >
          <option value="">{t('inventory.nobody')}</option>
          {members.map((member) => (
            <option key={member.userId} value={member.userId}>
              {member.displayName}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field__label" htmlFor={holderId}>
          {t('inventory.holder_label')}
        </label>
        <select
          id={holderId}
          className="field__control"
          value={draft.holderId}
          onChange={(changed) => setDraft({ ...draft, holderId: changed.target.value })}
        >
          <option value="">{t('inventory.nobody')}</option>
          {members.map((member) => (
            <option key={member.userId} value={member.userId}>
              {member.displayName}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field__label" htmlFor={expenseId}>
          {t('inventory.expense_label')}
        </label>
        <select
          id={expenseId}
          className="field__control"
          value={draft.transactionId}
          onChange={(changed) => setDraft({ ...draft, transactionId: changed.target.value })}
        >
          {/* "Nothing" first and named. Most kit predates the workspace or was
              a gift, and most expenses buy nothing that gets a record. */}
          <option value="">{t('inventory.expense_none')}</option>
          {expenses.map((expense) => (
            <option key={expense.id} value={expense.id}>
              {expense.description}
            </option>
          ))}
        </select>
        <p className="field__hint">
          {linked ? t('inventory.expense_linked_hint') : t('inventory.expense_hint')}
        </p>
      </div>

      {/* Gone entirely while the books own them. An empty disabled field would
          read as something to fill in; absence reads as what it is. */}
      {linked ? null : (
        <>
          <TextField
            label={t('inventory.purchased_label')}
            type="date"
            value={draft.purchasedOn}
            onChange={(changed) => setDraft({ ...draft, purchasedOn: changed.target.value })}
          />
          <MoneyInput
            label={t('inventory.cost_label')}
            currencyLabel={t('inventory.currency_label')}
            value={draft.amount}
            currency={draft.currency}
            currencies={[...currencies]}
            {...(amountError === null ? {} : { error: amountError })}
            onValueChange={(value) => setDraft({ ...draft, amount: value })}
            onCurrencyChange={(value) => setDraft({ ...draft, currency: value })}
          />
        </>
      )}

      <TextField
        label={t('inventory.notes_label')}
        value={draft.notes}
        maxLength={ITEM_NOTES_MAX}
        onChange={(changed) => setDraft({ ...draft, notes: changed.target.value })}
      />

      {error ? (
        <Callout tone="danger">
          {error instanceof NetworkError ? t('error.network') : t('inventory.refused')}
        </Callout>
      ) : null}

      <div className="btn-row">
        <Button variant="primary" type="submit" busy={pending}>
          {item ? t('inventory.save') : t('inventory.submit')}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          {t('inventory.cancel')}
        </Button>
      </div>
    </form>
  );
}
